import React from 'react';

const Gemini3Launch = () => (
  <article className="news-article-body">
    <p className="news-lead">
      Google's <strong>Gemini 3</strong>, released on <strong>November 18, 2025</strong>,
      represents the most comprehensive unification of Google's AI capabilities into a single
      model family — combining multimodality, long context, extended reasoning, and native tool
      use. The February 2026 <strong>Gemini 3 Deep Think</strong> update pushed the frontier
      further with a specialised mode for science and research challenges.
    </p>

    <h2>Gemini 3: What Changed From 2.5</h2>
    <p>
      Gemini 2.5 (GA June 2025) had already established Google's position at the frontier:
      Pro-level reasoning, 1M-token context windows, and Deep Think mode for mathematics and
      coding. Gemini 3 built on this foundation with improvements across:
    </p>
    <ul>
      <li><strong>Multimodal reasoning</strong>: Better cross-modal inference between text, images, audio, and video</li>
      <li><strong>Agentic capability</strong>: Native tool use and multi-step task orchestration</li>
      <li><strong>Coding performance</strong>: Improved on competitive programming benchmarks</li>
      <li><strong>Prompt injection defence</strong>: Enhanced security against adversarial input attacks</li>
    </ul>

    <h2>Gemini 3 Deep Think (February 2026)</h2>
    <p>
      Released as a specialised update in February 2026, <strong>Deep Think</strong> is designed
      for problems with incomplete data, unclear solutions, or requiring multi-hypothesis
      reasoning — the category of problems where even expert humans struggle. Target domains:
    </p>
    <ul>
      <li><strong>Scientific research</strong>: Hypothesis generation, literature synthesis, experiment design</li>
      <li><strong>Engineering</strong>: Complex systems analysis, failure mode exploration</li>
      <li><strong>Mathematics</strong>: Multi-step proofs and formal verification</li>
    </ul>
    <p>
      Deep Think is available to <strong>AI Ultra subscribers</strong> in the Gemini app, with
      early API access available to researchers and enterprises through Google AI Studio and
      Vertex AI.
    </p>

    <h2>Gemini App — New Capabilities</h2>
    <p>
      Alongside the model release, Google updated the Gemini app with:
    </p>
    <ul>
      <li>
        <strong>Gemini Agent</strong> (Ultra subscribers): Handles complex multi-step tasks
        autonomously — inbox management, travel booking, cross-application workflows
      </li>
      <li>
        <strong>Auto Browse</strong> in Chrome: Gemini 3 powers a Chrome side panel that can
        browse and summarise web content agentic ally on the user's behalf
      </li>
      <li>
        <strong>Dynamic View and Visual Layout</strong>: Generative UI that adapts its output
        format to the task — tables, code, visual timelines — rather than always returning plain text
      </li>
    </ul>

    <h2>The Gemini 2.5 Family That Preceded It</h2>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">Gemini 2.5 Pro</span>
        <span className="news-spec-value">Most capable — complex reasoning, 1M context</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Gemini 2.5 Flash</span>
        <span className="news-spec-value">Speed + efficiency — high-throughput enterprise</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Gemini 2.5 Flash-Lite</span>
        <span className="news-spec-value">Most cost-efficient — classification, routing</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Gemini 3 (Nov 2025)</span>
        <span className="news-spec-value">Unified reasoning + tool use + multimodal</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Gemini 3 Deep Think (Feb 2026)</span>
        <span className="news-spec-value">Science + research specialist mode</span>
      </div>
    </div>

    <h2>Nano Banana — The Image Generation Sibling</h2>
    <p>
      Alongside its text reasoning advances, Google also shipped <strong>Nano Banana</strong>
      (officially Gemini 3.1 Flash Image) in February 2026 — a high-speed AI image generation
      model that went viral for its quality-to-speed ratio. See the dedicated Nano Banana
      article for details.
    </p>

    <h2>Strategic Context: Google vs the Frontier</h2>
    <p>
      Google's Gemini 3 arrival came amid intense competition from OpenAI's o3, Anthropic's
      Claude Opus 4.6, and xAI's Grok 4. Unlike its competitors, Google has a unique
      distribution advantage: Gemini 3 ships integrated into Search, Chrome, Android, Gmail,
      Docs, and Workspace — reaching billions of users without requiring any opt-in.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        Gemini 3's most important feature may be its distribution, not its benchmarks.
        While frontier reasoning capabilities matter for technical users, Google's ability
        to embed Gemini 3 into products used by billions gives it a scale advantage no
        other AI lab can replicate. Deep Think, however, positions Google directly for
        the enterprise and research market where API-based reasoning quality is paramount.
      </p>
    </div>
  </article>
);

export default Gemini3Launch;
